import { API_BASE_URL, VERIFICATION_API_URL } from '../config/api.config';
import { apiRequest } from './api.interceptor';
import { getRuntimeConfig } from '../utils/runtime-config';

export interface VerificationRequest {
  corporate_email: string;
  company_id: string;
}

export interface VerificationCodeRequest {
  verification_id: string;
  code: string;
}

export interface VerificationResponse {
  verification_id?: string;
  success: boolean;
  message: string;
  badge_info?: {
    type: string;
    display_name: string;
    company: string | null;
  };
  referral_access?: boolean;
}

export interface VerificationStatus {
  is_verified: boolean;
  corporate_email?: string;
  company_name?: string;
  verification_date?: string;
  badge_type?: string;
  referral_access_enabled: boolean;
}

export interface CompanyDomains {
  company_id: string;
  company_name: string;
  domains: string[];
  auto_detected: boolean;
}

export interface CompanySearchResponse {
  found: boolean;
  company?: {
    id: string;
    name: string;
    domains: string[];
    verification_enabled: boolean;
  };
  can_verify: boolean;
}

class VerificationService {
  private readonly VERIFICATION_KEY = 'jobometry_verification_status';
  
  // Get cached verification status
  getCachedVerificationStatus(): VerificationStatus | null {
    try {
      const cached = localStorage.getItem(this.VERIFICATION_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error reading cached verification status:', error);
    }
    return null;
  }
  
  // Save verification status to cache
  private saveVerificationStatus(status: VerificationStatus): void {
    try {
      localStorage.setItem(this.VERIFICATION_KEY, JSON.stringify(status));
    } catch (error) {
      console.error('Error saving verification status:', error);
    }
  }
  
  // Clear cached verification status
  clearVerificationStatus(): void {
    try {
      localStorage.removeItem(this.VERIFICATION_KEY);
    } catch (error) {
      console.error('Error clearing verification status:', error);
    }
  }

  private isMockMode(): boolean {
    return localStorage.getItem('USE_MOCK_AUTH') === 'true'
      || getRuntimeConfig('REACT_APP_USE_MOCK_AUTH') === 'true';
  }

  async initiateVerification(data: VerificationRequest): Promise<VerificationResponse> {
    // Mock mode: skip real email sending
    if (this.isMockMode()) {
      return {
        success: true,
        verification_id: 'mock-' + Date.now(),
        message: 'Verification code sent (mock mode — use any 6-digit code)',
      };
    }

    try {
      // Get company name from domains data
      const companyInfo = await this.getCompanyDomains(data.company_id);

      // Use verification service for actual email sending
      const response = await apiRequest(`${VERIFICATION_API_URL}/verification/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          corporate_email: data.corporate_email,
          company_name: companyInfo.company_name || data.company_id,
          company_id: data.company_id
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to initiate verification');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Verification initiation error:', error);
      throw error;
    }
  }

  async verifyCode(data: VerificationCodeRequest): Promise<VerificationResponse> {
    // Mock mode: accept any 6-digit code
    if (this.isMockMode()) {
      if (data.code.length === 6) {
        return { success: true, message: 'Verified (mock mode)' };
      }
      return { success: false, message: 'Invalid code' };
    }

    try {
      const response = await apiRequest(`${VERIFICATION_API_URL}/verification/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to verify code');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Code verification error:', error);
      throw error;
    }
  }

  async resendCode(verificationId: string): Promise<VerificationResponse> {
    if (this.isMockMode()) {
      return {
        success: true,
        verification_id: 'mock-' + Date.now(),
        message: 'Code resent (mock mode)',
      };
    }

    try {
      const response = await apiRequest(`${API_BASE_URL}/verification/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_id: verificationId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to resend code');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Resend code error:', error);
      throw error;
    }
  }

  async getVerificationStatus(): Promise<VerificationStatus> {
    try {
      // For mock auth, check localStorage or env
      if (this.isMockMode()) {
        const cached = this.getCachedVerificationStatus();
        if (cached) {
          return cached;
        }
        // Return unverified status if no cached data
        return {
          is_verified: false,
          referral_access_enabled: false
        };
      }
      
      const response = await apiRequest(`${API_BASE_URL}/verification/status`);

      if (!response.ok) {
        // If not authenticated or server error, return unverified status
        return this.getCachedVerificationStatus() || {
          is_verified: false,
          referral_access_enabled: false
        };
      }
      
      const status = await response.json();
      // Cache the status for future use
      this.saveVerificationStatus(status);
      return status;
    } catch (error: any) {
      console.error('Get verification status error:', error);
      // Return cached status if available when API fails
      const cached = this.getCachedVerificationStatus();
      if (cached) {
        return cached;
      }
      throw error;
    }
  }
  
  // Mock method for completing verification (used in mock auth mode)
  async completeVerification(companyName: string, email: string): Promise<VerificationStatus> {
    const status: VerificationStatus = {
      is_verified: true,
      corporate_email: email,
      company_name: companyName,
      verification_date: new Date().toISOString(),
      badge_type: 'verified_employee',
      referral_access_enabled: true
    };
    
    this.saveVerificationStatus(status);
    return status;
  }

  async searchCompanyByEmail(email: string): Promise<CompanySearchResponse> {
    // Mock mode: match email domain against popular companies
    if (this.isMockMode()) {
      const domain = email.split('@')[1]?.toLowerCase();
      if (!domain) return { found: false, can_verify: false };

      const { popularCompanies } = await import('../data/popularCompanies');
      const match = popularCompanies.find(c =>
        c.domains.some(d => d.toLowerCase() === domain)
      );

      // Also check jobometry domains
      if (!match && (domain === 'jobometry.io' || domain === 'jobometry.com')) {
        return {
          found: true,
          company: { id: 'jobometry', name: 'Jobometry', domains: ['jobometry.io', 'jobometry.com'], verification_enabled: true },
          can_verify: true,
        };
      }

      if (match) {
        return {
          found: true,
          company: { id: match.id, name: match.name, domains: match.domains, verification_enabled: true },
          can_verify: true,
        };
      }
      return { found: false, can_verify: false };
    }

    try {
      const response = await apiRequest(`${VERIFICATION_API_URL}/companies/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to search company');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Company search error:', error);
      return {
        found: false,
        can_verify: false
      };
    }
  }

  async getCompanyDomains(companyId: string): Promise<CompanyDomains> {
    try {
      // Build mock companies from popular companies data + extras
      const { popularCompanies } = await import('../data/popularCompanies');
      const mockCompanies: Record<string, CompanyDomains> = {};
      for (const c of popularCompanies) {
        mockCompanies[c.id] = {
          company_id: c.id,
          company_name: c.name,
          domains: c.domains,
          auto_detected: true,
        };
      }
      // Extra companies not in directory
      mockCompanies['jobometry'] = {
        company_id: 'jobometry',
        company_name: 'Jobometry',
        domains: ['jobometry.io', 'jobometry.com'],
        auto_detected: true,
      };
      
      return mockCompanies[companyId] || {
        company_id: companyId,
        company_name: 'Unknown Company',
        domains: [],
        auto_detected: false
      };
    } catch (error: any) {
      console.error('Get company domains error:', error);
      throw error;
    }
  }
}

export const verificationService = new VerificationService();